import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FolderPlus, Check, Loader2, Plus } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddToCollectionButton = ({ productTitle, size = 'sm' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);

  const fetchCollections = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/collections`);
      setCollections(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);

  const handleAdd = async (collectionId, e) => {
    e?.stopPropagation();
    if (!user) { navigate('/login'); return; }
    setAdding(collectionId);
    try {
      await axios.post(`${API}/collections/${collectionId}/products/${encodeURIComponent(productTitle)}`);
      setCollections(prev => prev.map(c =>
        c.id === collectionId && !c.product_titles.includes(productTitle)
          ? { ...c, product_titles: [...c.product_titles, productTitle] }
          : c
      ));
    } catch { /* ignore */ }
    setAdding(null);
  };

  const sizeClasses = size === 'lg' ? 'w-10 h-10' : 'w-8 h-8';

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) fetchCollections(); }}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`add-to-collection-${productTitle.toLowerCase().replace(/\s+/g, '-')}`}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (!user) navigate('/login'); }}
          aria-label={`Add ${productTitle} to a collection`}
          className={`${sizeClasses} rounded-full flex items-center justify-center bg-white/80 backdrop-blur-sm text-stone-400 hover:text-primary hover:bg-white shadow-sm hover:shadow-md transition-all`}
        >
          <FolderPlus className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-52 rounded-xl border border-stone-100 bg-white shadow-lg p-1" data-testid="collection-dropdown">
        <div className="px-3 py-2">
          <p className="font-sans text-xs font-bold text-stone-500 uppercase tracking-wider">Add to collection</p>
        </div>
        <DropdownMenuSeparator className="bg-stone-100" />
        {loading ? (
          <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 text-stone-400 animate-spin" /></div>
        ) : collections.length === 0 ? (
          <div className="px-3 py-3 text-center">
            <p className="font-sans text-xs text-stone-400 mb-2">No collections yet</p>
          </div>
        ) : (
          collections.map(c => {
            const inCollection = c.product_titles?.includes(productTitle);
            return (
              <DropdownMenuItem
                key={c.id}
                data-testid={`collection-option-${c.id}`}
                onClick={(e) => { if (!inCollection) handleAdd(c.id, e); }}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg font-sans text-sm ${inCollection ? 'text-green-600' : 'text-stone-600 cursor-pointer'}`}
              >
                <span className="truncate">{c.name}</span>
                {adding === c.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                ) : inCollection ? (
                  <Check className="w-3.5 h-3.5 flex-shrink-0" />
                ) : null}
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator className="bg-stone-100" />
        <DropdownMenuItem
          data-testid="create-collection-from-dropdown"
          onClick={() => navigate('/collections')}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-primary font-sans text-sm font-medium cursor-pointer"
        >
          <Plus className="w-3.5 h-3.5" /> New collection
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToCollectionButton;
